import { ArrowLeft, Briefcase, Calendar, Copy, Eye, MapPin, Save, Search, Settings as SettingsIcon, Upload, UserRound } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";

import Spinner from "../../components/Spinner";
import {
  useJobBoardSettings,
  useSaveJobBoardSettings,
} from "../../hooks/useJobBoard";
import { JobBoardSettingsDto } from "../../types/jobBoard";

const EMPTY: JobBoardSettingsDto = {
  jobBoardSlug: "",
  recruitmentEmail: "",
  isPublic: true,

  candidateDisclaimer:
    "By submitting this application, you confirm that the information and documents provided are accurate and complete to the best of your knowledge. Your application and CV/resume may be reviewed by the organisation responsible for the vacancy and, where applicable, shared with relevant parties involved in the recruitment process. You may be contacted regarding this position or other relevant employment opportunities.\n\nSubmission of an application does not guarantee an interview, employment, or placement. Personal information will be processed in accordance with the applicable Privacy Policy and relevant data protection requirements.",

  privacyPolicyUrl: "",

  primaryColor: "#172554",
  secondaryColor: "#e2e8f0",
  buttonColor: "#7f1d1d",
  accentColor: "#be123c",
  logoUrl: "",
};

export default function JobBoardSettingsPage() {
  const query = useJobBoardSettings();
  const save = useSaveJobBoardSettings();
  const [previewMode, setPreviewMode] = useState<"board" | "details" | "apply">("board");

  const [values, setValues] = useState<JobBoardSettingsDto>(() => ({
    ...EMPTY,
  }));
  const [errors, setErrors] = useState<{
    jobBoardSlug?: string;
    recruitmentEmail?: string;
    privacyPolicyUrl?: string;
  }>({});

  useEffect(() => {
    if (!query.data) return;

    const nextValues: JobBoardSettingsDto = {
      ...query.data,

      candidateDisclaimer:
        query.data.candidateDisclaimer?.trim() ||
        EMPTY.candidateDisclaimer,

      privacyPolicyUrl:
        query.data.privacyPolicyUrl ?? "",
    };
    
    setValues(nextValues);
  }, [query.data]);

  const change = (
    field: keyof JobBoardSettingsDto,
    value: string | boolean
  ) => {
    setValues((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const baseUrl = window.location.origin;

  const publicUrl = values.publicUrl
    ? `${baseUrl}${values.publicUrl}`
    : `${baseUrl}/jobs/${values.jobBoardSlug}`;

  const embedUrl = values.embedUrl
    ? `${baseUrl}${values.embedUrl}`
    : `${baseUrl}/jobs/${values.jobBoardSlug}/embed`;

  const embedCode =
    values.embedCode ??
    `${embedUrl}</iframe>`;

  const copy = (text: string) =>
    navigator.clipboard.writeText(text).then(() => {
      toast.success("Copied to clipboard");
    });

  const submit = (event: React.FormEvent) => {
    event.preventDefault();

    if (
      !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(values.jobBoardSlug)
    ) {
      setErrors((current) => ({
        ...current,
        jobBoardSlug:
          "Use lowercase letters, numbers, and hyphens only.",
      }));
      return;
    }

    if (
      !values.recruitmentEmail ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
        values.recruitmentEmail
      )
    ) {
      setErrors((current) => ({
        ...current,
        recruitmentEmail: "Please enter a valid recruitment email.",
      }));
      return;
    }

    setErrors({});
    save.mutate(values);
  };

  if (query.isLoading) {
    return (
      <div className="flex justify-center p-12">
        <Spinner />
      </div>
    );
  }

  function JobBoardPreview({values,}: {
    values: JobBoardSettingsDto;
  }) {
    return (
      <div
        className="min-h-[600px] bg-slate-50"
        style={
          {
            "--preview-primary":
              values.primaryColor || "#172554",
            "--preview-secondary":
              values.secondaryColor || "#475569",
            "--preview-button":
              values.buttonColor || "#172554",
            "--preview-accent":
              values.accentColor || "#e11d48",
          } as React.CSSProperties
        }
      >
        {/* Header */}
        <div className="border-b border-slate-200 bg-white p-4">
          <div className="flex items-center gap-3">
            {/* {values.logoUrl ? (
              {values.logoUrl}
            ) : ( */}
              <div
                className="flex h-10 w-10 items-center justify-center rounded-lg text-white"
                style={{
                  backgroundColor:
                    "var(--preview-primary)",
                }}
              >
                <Briefcase size={18} />
              </div>
            {/* )} */}

            <div>
              <p
                className="text-[9px] font-bold uppercase tracking-widest"
                style={{
                  color: "var(--preview-accent)",
                }}
              >
                Careers
              </p>

              <p
                className="text-sm font-bold"
                style={{
                  color: "var(--preview-primary)",
                }}
              >
                {values.companyName ||
                  values.jobBoardSlug ||
                  "Your Job Board"}
              </p>
            </div>
          </div>
        </div>

        <div className="p-4">
          {/* Hero */}
          <div
            className="rounded-2xl px-5 py-8 text-center text-white"
            style={{
              backgroundColor:
                "var(--preview-primary)",
            }}
          >
            <h3 className="text-xl font-bold text-white">
              Build Your Future With Us
            </h3>

            <p className="mx-auto mt-2 max-w-xs text-xs leading-5 text-white/80">
              Join a team focused on growth, innovation, and
              delivering exceptional solutions.
            </p>

            <span className="mt-4 inline-flex rounded-full bg-white/10 px-3 py-1.5 text-[10px] font-semibold">
              2 Open Positions
            </span>
          </div>

          {/* Search */}
          <div className="mt-4 rounded-xl bg-white p-3 shadow-sm">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />

              <div className="rounded-lg border border-slate-200 py-2.5 pl-9 text-xs text-slate-400">
                Search jobs...
              </div>
            </div>

            <div className="mt-2 grid grid-cols-2 gap-2">
              <div className="rounded-lg border border-slate-200 px-3 py-2.5 text-[10px] text-slate-400">
                Filter industries...
              </div>

              <div className="rounded-lg border border-slate-200 px-3 py-2.5 text-[10px] text-slate-400">
                Filter locations...
              </div>
            </div>
          </div>

          {/* Job Cards */}
          <div className="mt-4 space-y-3">
            <PreviewJobCard
              values={values}
              title="Frontend Developer"
              industry="Technology"
              country="United Kingdom"
            />
          </div>
        </div>
      </div>
    );
  }

  function PreviewJobCard({
    values,
    title,
    industry,
    country,
  }: {
    values: JobBoardSettingsDto;
    title: string;
    industry: string;
    country: string;
  }) {
    const accent =
      values.accentColor || "#e11d48";

    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex items-start justify-between gap-3">
          <h4
            className="text-sm font-bold"
            style={{
              color:
                values.primaryColor || "#172554",
            }}
          >
            {title}
          </h4>

          <span
            className="rounded-lg px-2 py-1 text-[9px] font-semibold text-white"
            style={{
              backgroundColor:
                values.primaryColor || "#172554",
            }}
          >
            Open
          </span>
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {[industry, country].map((item) => (
            <span
              key={item}
              className="rounded-full px-2 py-1 text-[9px] font-semibold"
              style={{
                backgroundColor: `${accent}20`,
                color: accent,
              }}
            >
              {item}
            </span>
          ))}
        </div>

        <p className="mt-3 text-[11px] leading-5 text-slate-500">
          Join our growing team and help us deliver exceptional
          solutions to our customers.
        </p>

        <div className="mt-3 flex flex-wrap gap-3 text-[9px] text-slate-400">
          <span className="flex items-center gap-1">
            <Briefcase size={11} />
            {industry}
          </span>

          <span className="flex items-center gap-1">
            <MapPin size={11} />
            {country}
          </span>

          <span className="flex items-center gap-1">
            <Calendar size={11} />
            Published Today
          </span>
        </div>

        <div className="mt-4 border-t border-slate-100 pt-3">
          <span
            className="inline-block rounded-lg px-3 py-2 text-[10px] font-semibold text-white"
            style={{
              backgroundColor:
                values.buttonColor || "#172554",
            }}
          >
            View Details
          </span>
        </div>
      </div>
    );
  }

  function JobDetailsPreview({
    values,
  }: {
    values: JobBoardSettingsDto;
  }) {
    const accent =
      values.accentColor || "#e11d48";

    return (
      <div className="min-h-[600px] bg-slate-50">
        {/* Back header */}
        <div className="border-b border-slate-200 bg-white p-4 text-right">
          <span
            className="inline-flex items-center gap-1.5 text-[10px] font-semibold"
            style={{ color: accent }}
          >
            <ArrowLeft size={12} />
            All Positions
          </span>
        </div>

        <div className="p-5">
          <p
            className="text-[9px] font-bold uppercase tracking-widest"
            style={{ color: accent }}
          >
            Career Opportunity
          </p>

          <h3
            className="mt-1 text-xl font-bold"
            style={{
              color:
                values.primaryColor || "#172554",
            }}
          >
            Frontend Developer
          </h3>

          <div className="mt-3 flex flex-wrap gap-2">
            {["Technology", "United Kingdom"].map(
              (item) => (
                <span
                  key={item}
                  className="rounded-full px-2.5 py-1 text-[9px] font-semibold"
                  style={{
                    backgroundColor: `${accent}20`,
                    color: accent,
                  }}
                >
                  {item}
                </span>
              )
            )}
          </div>

          <div className="mt-5 space-y-4">
            {/* Description */}
            <article className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h4
                className="text-sm font-bold"
                style={{
                  color:
                    values.primaryColor || "#172554",
                }}
              >
                Job Description
              </h4>

              <p className="mt-2 text-[11px] leading-5 text-slate-500">
                We're looking for a Frontend Developer to help
                build modern, accessible, and engaging digital
                experiences for our customers.
              </p>

              <div className="mt-4 border-t border-slate-100 pt-4">
                <h4
                  className="text-sm font-bold"
                  style={{
                    color:
                      values.primaryColor || "#172554",
                  }}
                >
                  Requirements
                </h4>

                <ul className="mt-2 list-disc space-y-1 pl-4 text-[11px] leading-5 text-slate-500">
                  <li>Experience building modern web applications</li>
                  <li>Strong attention to quality and usability</li>
                  <li>Excellent communication skills</li>
                </ul>
              </div>
            </article>

            {/* Apply Card */}
            <aside className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <h4 className="text-sm font-bold text-slate-900">
                Interested in this role?
              </h4>

              <p className="mt-1 text-[11px] text-slate-500">
                Share your details and resume with the hiring team.
              </p>

              <div className="mt-3 space-y-2 text-[10px] text-slate-500">
                <div className="flex items-center gap-2">
                  <Briefcase size={12} />
                  Technology
                </div>

                <div className="flex items-center gap-2">
                  <MapPin size={12} />
                  United Kingdom
                </div>

                <div className="flex items-center gap-2">
                  <Calendar size={12} />
                  Published Today
                </div>
              </div>

              <button
                type="button"
                onClick={() => {}}
                className="mt-4 w-full rounded-lg px-3 py-2.5 text-xs font-semibold text-white"
                style={{
                  backgroundColor:
                    values.buttonColor || "#172554",
                }}
              >
                Apply Now
              </button>
            </aside>
          </div>
        </div>
      </div>
    );
  }

  function JobApplyPreview({
    values,
  }: {
    values: JobBoardSettingsDto;
  }) {
    return (
      <div className="min-h-[600px] bg-slate-50 p-5">
        <div className="rounded-xl bg-white p-5 shadow-sm">
          <div className="mb-5 border-b border-slate-100 pb-4">
            <div className="flex items-center gap-2">
              <UserRound
                size={17}
                style={{
                  color:
                    values.accentColor || "#e11d48",
                }}
              />

              <h3 className="text-base font-bold text-slate-900">
                Application Details
              </h3>
            </div>

            <p className="mt-1 text-[11px] text-slate-500">
              Complete the form below and upload your resume.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <PreviewInput label="First Name *" />
            <PreviewInput label="Last Name *" />
          </div>

          <div className="mt-3">
            <PreviewInput label="Email Address *" />
          </div>

          <div className="mt-3">
            <PreviewInput label="Contact Number" />
          </div>

          <div className="mt-3">
            <p className="mb-1 text-[10px] font-semibold text-slate-700">
              Cover Letter
            </p>

            <div className="h-20 rounded-lg border border-slate-200 bg-white" />
          </div>

          {/* Resume */}
          <div className="mt-3">
            <p className="mb-1 text-[10px] font-semibold text-slate-700">
              Resume *
            </p>

            <div
              className="flex items-center gap-2 rounded-lg border border-dashed bg-slate-50 p-3 text-[10px] text-slate-500"
              style={{
                borderColor:
                  values.buttonColor || "#cbd5e1",
              }}
            >
              <Upload size={14} />
              Choose a resume file
            </div>
          </div>

          {/* Disclaimer */}
          {values.candidateDisclaimer && (
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-[9px] leading-4 text-slate-600">
              <p className="mb-1 font-semibold text-slate-700">
                Application Disclaimer
              </p>

              <p className="whitespace-pre-wrap">
                {values.candidateDisclaimer}
              </p>

              {values.privacyPolicyUrl && (
                <p className="mt-2">
                  For more information about how your personal
                  information is handled, please review the{" "}
                  <span className="underline">
                    Privacy Policy
                  </span>
                  .
                </p>
              )}
            </div>
          )}

          <button
            type="button"
            className="mt-5 flex w-full items-center justify-center rounded-lg px-4 py-3 text-xs font-semibold text-white"
            style={{
              backgroundColor:
                values.buttonColor || "#172554",
            }}
          >
            Submit Application
          </button>
        </div>
      </div>
    );
  }

  function PreviewInput({
    label,
  }: {
    label: string;
  }) {
    return (
      <div>
        <p className="mb-1 text-[10px] font-semibold text-slate-700">
          {label}
        </p>

        <div className="h-9 rounded-lg border border-slate-200 bg-white" />
      </div>
    );
  }

  return (
    <div className="space-y-6 text-left">
      <header>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-strivre-navy">
          <SettingsIcon size={22} />
          Job Board Configuration
        </h1>

        <p className="mt-1 text-sm text-strivre-mutedtext">
          Control your public URL, brand colors, and embed configuration.
        </p>
      </header>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">

        {/* Settings Form */}
        <form
          onSubmit={submit}
          className="space-y-5 rounded-lg border border-strivre-lightnavy bg-white p-6"
        >
          <div>
            <label className="block text-sm font-medium text-strivre-darktext mb-1">
              Job Board Slug *
            </label>

            <input
              className="input-base"
              value={values.jobBoardSlug}
              onChange={(e) =>
                change(
                  "jobBoardSlug",
                  e.target.value.toLowerCase()
                )
              }
              placeholder="your-company"
            />

            {errors.jobBoardSlug && (
              <p className="error-text">
                {errors.jobBoardSlug}
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-strivre-darktext mb-1">
              Recruitment Email *
            </label>

            <input
              type="email"
              className="input-base"
              value={values.recruitmentEmail ?? ""}
              onChange={(e) =>
                change("recruitmentEmail", e.target.value)
              }
              placeholder="recruitment@yourcompany.com"
            />
            {errors.recruitmentEmail && (
              <p className="error-text">
                {errors.recruitmentEmail}
              </p>
            )}

            <p className="mt-1 text-xs text-slate-500">
              New job applications will be sent to this email address.
            </p>
          </div>
          <div className="border-t border-slate-100 pt-5">
          <div className="mb-4">
            <h2 className="text-base font-bold text-strivre-navy">
              Application Settings
            </h2>

            <p className="mt-1 text-xs text-slate-500">
              Configure the information shown to candidates when they submit an application.
            </p>
          </div>

          <div className="space-y-5">
            <div>
              <label className="mb-1 block text-sm font-medium text-strivre-darktext">
                Candidate Disclaimer
              </label>

              <textarea
                rows={7}
                className="input-base resize-y"
                value={values.candidateDisclaimer ?? ""}
                onChange={(e) =>
                  change("candidateDisclaimer", e.target.value)
                }
                placeholder="Enter the disclaimer displayed to candidates before submitting an application."
              />

              <p className="mt-1 text-xs text-slate-500">
                This text will appear above the Submit Application button.
              </p>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-strivre-darktext">
                Privacy Policy URL
              </label>

              <input
                type="url"
                className="input-base"
                value={values.privacyPolicyUrl ?? ""}
                onChange={(e) =>
                  change("privacyPolicyUrl", e.target.value)
                }
                placeholder="https://yourcompany.com/privacy"
              />

              {errors.privacyPolicyUrl && (
                <p className="error-text">
                  {errors.privacyPolicyUrl}
                </p>
              )}
              <p className="mt-1 text-xs text-slate-500">
                Candidates can use this link to review your organisation's Privacy Policy.
              </p>
            </div>
          </div>
        </div>

          <label className="flex items-center justify-between rounded-lg border border-slate-200 p-4">
            <span>
              <span className="block text-sm font-semibold text-slate-800">
                Public Job Board
              </span>

              <span className="text-xs text-slate-500">
                Allow candidates to view published jobs.
              </span>
            </span>

            <input
              type="checkbox"
              checked={values.isPublic}
              onChange={(e) =>
                change("isPublic", e.target.checked)
              }
              className="h-4 w-4 accent-strivre-burgundy"
            />
          </label>

          <div>
            <label className="block text-sm font-medium text-strivre-darktext mb-1">
              Brand Colors
            </label>

            <div className="grid grid-cols-2 gap-4">
              {(
                [
                  "primaryColor",
                  "secondaryColor",
                  "buttonColor",
                  "accentColor",
                ] as const
              ).map((field) => (
                <label
                  key={field}
                  className="flex items-center gap-3 text-xs font-semibold capitalize text-slate-600"
                >
                  <input
                    type="color"
                    value={values[field]}
                    onChange={(e) =>
                      change(field, e.target.value)
                    }
                    className="h-10 w-12 cursor-pointer rounded border-0 p-0"
                  />

                  {field.replace("Color", "")}
                </label>
              ))}
            </div>
          </div>

          {/* <div className="hidden">
            <label className="label-base">
              Logo URL
            </label>

            <input
              className="input-base"
              value={values.logoUrl ?? ""}
              onChange={(e) =>
                change("logoUrl", e.target.value)
              }
              placeholder="https://..."
            />
          </div> */}

          <button
            disabled={save.isPending}
            className="btn-primary btn-md inline-flex items-center gap-2 rounded-lg"
          >
            <Save size={17} />

            {save.isPending
              ? "Saving..."
              : "Save Settings"}
          </button>
        </form>

        {/* Sidebar */}
        <div className="space-y-5">

          {/* Share Block */}
          <div className="rounded-lg border border-strivre-lightnavy bg-white p-5">
            <h2 className="font-bold text-slate-900">
              Share Your Board
            </h2>

            {[
              ["Public URL", publicUrl],
              ["Embed URL", embedUrl],
              ["Embed Code", embedCode],
            ].map(([label, text]) => (
              <div key={label} className="mt-4">
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  {label}
                </p>

                <div className="flex items-start gap-2">
                  <code className="min-w-0 flex-1 break-all rounded bg-slate-50 p-2 text-xs text-slate-600">
                    {text}
                  </code>

                  <button
                    type="button"
                    title={`Copy ${label}`}
                    onClick={() => copy(text)}
                    className="rounded-lg border border-slate-200 p-2 text-slate-500 hover:text-strivre-burgundy"
                  >
                    <Copy size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Preview */}
          <div className="overflow-hidden rounded-lg border border-strivre-lightnavy bg-white">
            <div className="border-b border-strivre-lightnavy px-5 py-4">
              <div className="flex items-center gap-2 font-bold text-strivre-navy">
                <Eye size={17} />
                Preview
              </div>

              <p className="mt-1 text-xs text-strivre-mutedtext">
                Preview the public job board experience.
              </p>
            </div>

            {/* Preview Navigation */}
            <div className="border-b border-strivre-lightnavy bg-strivre-softgrey p-2">
              <div className="grid grid-cols-3 gap-1">
                <button
                  type="button"
                  onClick={() => setPreviewMode("board")}
                  className={`rounded-lg px-2 py-2 text-xs font-semibold transition-colors ${
                    previewMode === "board"
                      ? "bg-strivre-burgundy text-white"
                      : "text-strivre-mutedtext hover:bg-white hover:text-strivre-burgundy"
                  }`}
                >
                  Job Board
                </button>

                <button
                  type="button"
                  onClick={() => setPreviewMode("details")}
                  className={`rounded-lg px-2 py-2 text-xs font-semibold transition-colors ${
                    previewMode === "details"
                      ? "bg-strivre-burgundy text-white"
                      : "text-strivre-mutedtext hover:bg-white hover:text-strivre-burgundy"
                  }`}
                >
                  Job Details
                </button>

                <button
                  type="button"
                  onClick={() => setPreviewMode("apply")}
                  className={`rounded-lg px-2 py-2 text-xs font-semibold transition-colors ${
                    previewMode === "apply"
                      ? "bg-strivre-burgundy text-white"
                      : "text-strivre-mutedtext hover:bg-white hover:text-strivre-burgundy"
                  }`}
                >
                  Apply
                </button>
              </div>
            </div>

            <div className="max-h-[680px] overflow-y-auto bg-slate-50">
              {previewMode === "board" && (
                <JobBoardPreview values={values} />
              )}

              {previewMode === "details" && (
                <JobDetailsPreview values={values} />
              )}

              {previewMode === "apply" && (
                <JobApplyPreview values={values} />
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}